import gql from "graphql-tag";
import { reportActionError } from "~/core/store/common/errors/handleStoreError.ts";
import {
  composeGiftOrderComment,
  getGiftOrderPreference,
} from "~/core/utils/giftOrder";
import { displayTotals } from "../../../../utils/checkout-totals.mjs";
import { taxAddressError } from "../../../../utils/checkout-tax-address.mjs";
import { serverPaymentMessage } from "../../../../utils/payment-submit.mjs";

export const CONFIRM_ORDER_MUTATION = gql`
  mutation confirmOrder($withPayment: Boolean = true, $comment: String) {
    confirmOrder(withPayment: $withPayment, comment: $comment) {
      order {
        id
        clientSecret
        paymentIntentId
      }
      url
      callback
    }
  }
`;
export const state = () => ({
  order: null,
  url: "",
  paymentError: null,
  details: null,
  updateOrderResponse: null,
  // "fresh": updateOrderResponse matches the session; "pending": an update is
  // running or queued; "stale": the cart changed on the information step and
  // the next update re-prices it; "failed": the last update failed.
  totalsStatus: "fresh",
  // The NY tax text for a shipping address Stripe Tax cannot locate, from the
  // last update that failed with it; the information step shows it at the
  // address. Cleared by a successful update or an edit of the address.
  taxAddressError: null,
  updateSeq: 0,
  lastInit: null,
  // The shopper text (SERVER_PAYMENT_MESSAGES) of the last initPayment
  // failure, or null; init still returns null on any failure.
  paymentServerMessage: null,
  // True while the payment step submits: controls that change the cart or the
  // order stay locked so the order cannot change under a confirmed payment.
  paymentBusy: false,
  confirmOrderDetails: null,
  isConfirmOrderSuccessfully: false,
  clientSecret: null,
  paymentIntentId: null,
  refreshToken: null,
});

export const getters = {
  order(state) {
    return state.order;
  },
  isConfirmOrderSuccessfully(state) {
    return state.isConfirmOrderSuccessfully;
  },
  url(state) {
    return state.url;
  },
  paymentError(state) {
    return state.paymentError;
  },
  details(state) {
    return state.details;
  },
  updateOrderResponse(state) {
    return state.updateOrderResponse;
  },
  totalsStatus(state) {
    return state.totalsStatus;
  },
  taxAddressError(state) {
    return state.taxAddressError;
  },
  paymentServerMessage(state) {
    return state.paymentServerMessage;
  },
  // Every checkout total on the page (summary, mobile header, review, pay
  // button) reads this one getter.
  displayTotals(state, _getters, _rootState, rootGetters) {
    return displayTotals({
      orderTotals: state.updateOrderResponse?.totals,
      cart: rootGetters["store/cart/get"],
      status: state.totalsStatus,
      currency: (rootGetters["store/currency/get"] || []).find?.(
        (currency) => currency.active
      ),
    });
  },
  // { payment, at }. Getters are cached, so callers judge the age themselves.
  lastInit(state) {
    return state.lastInit;
  },
  paymentBusy(state) {
    return state.paymentBusy;
  },
  confirmOrderDetails(state) {
    return state.confirmOrderDetails;
  },
  clientSecret(state) {
    return state.clientSecret;
  },
  paymentIntentId(state) {
    return state.paymentIntentId;
  },
  orderId(state) {
    return state.orderId;
  },
};

export const mutations = {
  setOrder(state, order) {
    state.order = order;
  },
  setIsConfirmOrderSuccessfully(state, payload) {
    state.isConfirmOrderSuccessfully = payload;
  },
  setUrl(state, payload) {
    state.url = payload;
  },
  setPaymentError(state, payload) {
    state.paymentError = payload;
  },
  clearPaymentError(state) {
    state.paymentError = null;
  },
  setOrderDetails(state, payload) {
    // Merge (not replace) so a partial commit — e.g. the billing-only
    // { paymentAddress, paymentAddressId } from new-payment-section — can never
    // silently drop the already-set shippingAddress / shippingAddressId /
    // shippingMethod / paymentMethod set by earlier steps.
    state.details = { ...state.details, ...payload };
  },
  setConfirmOrderDetails(state, payload) {
    state.confirmOrderDetails = payload;
  },
  setUpdateOrderResponse(state, payload) {
    state.updateOrderResponse = payload;
  },
  setTotalsStatus(state, status) {
    state.totalsStatus = status;
  },
  setTaxAddressError(state, message) {
    state.taxAddressError = message || null;
  },
  setPaymentServerMessage(state, message) {
    state.paymentServerMessage = message || null;
  },
  setUpdateSeq(state, seq) {
    state.updateSeq = seq;
  },
  setLastInit(state, payment) {
    state.lastInit = payment ? { payment, at: Date.now() } : null;
  },
  setPaymentBusy(state, busy) {
    state.paymentBusy = Boolean(busy);
  },
  setClientSecret(state, secret) {
    state.clientSecret = secret;
  },
  setPaymentIntentId(state, id) {
    state.paymentIntentId = id;
  },
  setOrderId(state, orderId) {
    state.orderId = orderId;
  },
  setRefreshToken(state, token) {
    state.refreshToken = token;
  },
  setPaymentIntentStatus(state, status) {
    state.paymentIntentStatus = status;
  },
  setStripePublicKey(state, stripePublicKey) {
    state.stripePublicKey = stripePublicKey;
  },
};

// Checkout callers that show their own status text pass { silent: true }: the
// raw server text then reaches neither a toast nor vuefront/error, which
// vf-o-form (it wraps the whole checkout) prints verbatim. Callers without
// options keep the toast.
const reportOrderError = (commit, error, silent) => {
  if (silent) {
    // eslint-disable-next-line no-console -- diagnostic only; the page shows its own text
    console.warn("[checkout] order request failed", error);
    return;
  }
  reportActionError({ commit }, error, { showNotification: true });
};

export const actions = {
  async express(_context, input) {
    const { data } = await this.$vfapollo.mutate({
      mutation: gql`
        mutation expressCheckout($input: String!) {
          expressCheckout(input: $input)
        }
      `,
      variables: { input: JSON.stringify(input) },
    });
    return JSON.parse(data.expressCheckout);
  },
  async create({ commit, state }) {
    // Checkout entry. A priced response, a "failed" status or an unused
    // payment init left from an earlier visit must not show on step 2, and an
    // update still in flight from then must not land. The details stay: they
    // prefill the form.
    commit("setUpdateSeq", state.updateSeq + 1);
    commit("setUpdateOrderResponse", null);
    commit("setTotalsStatus", "fresh");
    commit("setTaxAddressError", null);
    commit("setLastInit", null);
    try {
      await this.$vfapollo.mutate({
        mutation: gql`
          mutation {
            createOrder {
              success
            }
          }
        `,
      });
    } catch (e) {
      reportActionError({ commit }, e, { showNotification: true });
    }
  },

  // payload: { silent, restoreStatusOnFailure }. Old callers pass nothing or a
  // DOM event, so only an explicit true counts.
  async update({ commit, state, dispatch }, payload) {
    const silent = payload?.silent === true;
    // The pay click re-sends an unchanged order. If that request fails the
    // priced total on the page is still right, so it stays payable and the
    // next click simply runs the update and the total check again.
    const failedStatus =
      payload?.restoreStatusOnFailure === true && state.totalsStatus === "fresh"
        ? "fresh"
        : "failed";
    // Keep the last priced response on screen while this one runs: clearing it
    // made the shipping list vanish and the Total fall back to the cart
    // subtotal, and a failed update left it that way.
    const seq = state.updateSeq + 1;
    commit("setUpdateSeq", seq);
    commit("setTotalsStatus", "pending");
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation (
            $paymentAddress: [InputField]
            $paymentAddressId: String
            $shippingAddressId: String
            $shippingAddress: [InputField]
            $paymentMethod: String
            $shippingMethod: String
          ) {
            updateOrder(
              paymentAddress: $paymentAddress
              shippingAddress: $shippingAddress
              shippingAddressId: $shippingAddressId
              paymentAddressId: $paymentAddressId
              paymentMethod: $paymentMethod
              shippingMethod: $shippingMethod
            ) {
              shippingMethods {
                id
                codename
                name
              }
              totals {
                title
                text
              }
            }
          }
        `,
        variables: state.details,
      });
      // A newer update started while this one ran; its response owns the page.
      if (seq !== state.updateSeq) return data;
      if (data.updateOrder) commit("setUpdateOrderResponse", data.updateOrder);
      commit("setTotalsStatus", data.updateOrder ? "fresh" : failedStatus);
      if (data.updateOrder) commit("setTaxAddressError", null);
      if (data.updateOrder) {
        // A validated, saved checkout identity enables the consented guest
        // funnel. This is identification only, never a marketing subscription.
        void dispatch(
          "common/klaviyo/identifyCheckout",
          state.details?.shippingAddress || state.details?.paymentAddress,
          { root: true }
        ).catch(() => {});
      }
      return data;
    } catch (e) {
      if (seq === state.updateSeq) {
        commit("setTotalsStatus", failedStatus);
        commit("setTaxAddressError", taxAddressError(e));
      }
      reportOrderError(commit, e, silent);
    }
  },

  // payload: { silent } as for update.
  async init({ commit }, payload) {
    commit("setPaymentServerMessage", null);
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation initPayment {
            initPayment {
              refresh
              paymentIntentId
              clientSecret
              stripePublicKey
              paymentIntentStatus
            }
          }
        `,
      });

      const paymentData = data?.initPayment;
      commit("setClientSecret", paymentData.clientSecret);
      commit("setPaymentIntentId", paymentData.paymentIntentId);
      commit("setPaymentIntentStatus", paymentData.paymentIntentStatus);
      commit("setStripePublicKey", paymentData.stripePublicKey);

      if (paymentData.refresh) {
        commit("setRefreshToken", paymentData.refresh);
      }
      commit("setLastInit", paymentData);
      return paymentData;
    } catch (e) {
      commit("setPaymentServerMessage", serverPaymentMessage(e));
      reportOrderError(commit, e, payload?.silent === true);
      return null;
    }
  },

  // silent: the payment step shows its own modal, so the failure must not also
  // reach vuefront/error, which vf-o-form prints (an Apollo transport error
  // even makes that form throw while rendering and hides the whole checkout).
  async confirm(
    { commit, state },
    { elements, withPayment, onSuccess, silent }
  ) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: CONFIRM_ORDER_MUTATION,
        variables: {
          withPayment,
          comment: composeGiftOrderComment(
            state.details?.comment,
            getGiftOrderPreference()
          ),
        },
      });

      const { order, url, callback } = data.confirmOrder;

      commit("setOrder", order);
      commit("setOrderId", order.id);
      commit("setUrl", url);
      // commit("setClientSecret", order.clientSecret);
      commit("setPaymentIntentId", order.paymentIntentId);
      if (typeof onSuccess === "function") {
        onSuccess({
          order,
          clientSecret: order.clientSecret,
          paymentIntentId: order.paymentIntentId,
          callback,
          url,
        });
      }

      return {
        status: "succeed",
        order,
        clientSecret: order.clientSecret,
        paymentIntentId: order.paymentIntentId,
        callback,
        url,
      };
    } catch (e) {
      console.error("GraphQL confirmOrder error:", e?.message || e);
      // No toast here: the checkout UI shows its own error modal from the
      // returned { error, message } object.
      if (silent !== true) reportActionError({ commit }, e);
      return {
        error: true,
        message: e?.message || "Unknown GraphQL error",
        // A shopper text from SERVER_PAYMENT_MESSAGES, or null.
        serverMessage: serverPaymentMessage(e),
      };
    }
  },

  async confirmKlarna(
    { commit, state },
    { withPayment, paymentMethod, onSuccess }
  ) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: CONFIRM_ORDER_MUTATION,
        variables: {
          withPayment,
          paymentMethod,
          comment: composeGiftOrderComment(
            state.details?.comment,
            getGiftOrderPreference()
          ),
        },
      });

      const { order, url, clientSecret, paymentIntentId } = data.confirmOrder;

      commit("setOrder", order);
      commit("setUrl", url);
      commit("setClientSecret", clientSecret);
      commit("setPaymentIntentId", paymentIntentId);

      if (typeof onSuccess === "function") {
        onSuccess({
          order,
          clientSecret,
          paymentIntentId,
        });
      }

      return {
        order,
        clientSecret,
        paymentIntentId,
      };
    } catch (e) {
      console.error("GraphQL Klarna error:", e?.message || e);
      // No toast here: the checkout UI shows its own error modal from the
      // returned { error, message } object.
      reportActionError({ commit }, e);
      return { error: true, message: e?.message || "Unknown GraphQL error" };
    }
  },
};
export const namespaced = true;
//# sourceMappingURL=order.mjs.map
